import {emotionStyled, tokens} from '@packages/utilities/styled';

import {IconButton} from '../../IconButton';

import {RowItemAdornmentView} from '../RowItemAdornment/RowItemAdornment.styles';
import {RowItemActiveSelectors} from '../RowItem.styles';
import {disabledSelector} from '../../../utils';

export const RowItemActionsPortal = emotionStyled('div')(
  {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: 'initial',
    lineHeight: 'initial',
    display: 'flex',
    flexDirection: 'row',
    /**
     * We turn off pointer-events at the portal wrapper layer so clicks will pass through to the `RowItemBody`.
     * They are turned back on for individual actions in `RowItemActionView` below and for Adornments (selectively) in `RowItemAdornmentView` (located in `RowItemAdornment.styles.ts`).
     */
    pointerEvents: 'none',
    /**
     * There should be a `$1` gap between Adornments/Actions and the Chevron.
     */
    gap: '$1',
  },
  {
    componentName: 'RowItemActionsPortal',
    variants: {
      density: {
        comfortable: {
          right: tokens.ui.rowItem.padding.comfortable.right,
        },
        compact: {
          right: tokens.ui.rowItem.padding.compact.right,
        },
      },
    },
    defaultVariants: {
      density: 'comfortable',
    },
  }
);

export const RowItemActionView = emotionStyled(IconButton)(
  {
    pointerEvents: 'auto',
    /**
     * If any Adornments are present, we want to add a space between them and the first Action.
     */
    [`${RowItemAdornmentView} + &`]: {
      marginLeft: '$1',
    },
  },
  {
    componentName: 'RowItemAction',
    variants: {
      /**
       * Even though this is the "default" behavior, we are using a variant here because it is easier than overriding them in the special case.
       *
       */
      shouldHideAction: {
        true: {
          opacity: 0,
          visibility: 'hidden',
          position: 'absolute',
          /**
           * These transitions are necessary to allow tab-order to work correctly in Chrome and Firefox.
           *
           * Without them, if we focus on the `RowItemBody` (the default focus target) we will see our Actions appear correctly.
           * However, when we press tab, we will lose `focus-visible` for a split second and the Actions will disappear.
           * This does not happen in Safari.
           *
           * Therefore, what we need to do is basically tell `visibility` to delay for a split second, just long enough for Chrome and Firefox to re-apply their focus states onto the `RowItemAction`.
           * At which point, we will have `:focus-visible` again and trigger our selector below as before.
           *
           * @see https://docs.google.com/document/d/1qAKSxrMJJ9_RsQ5Gds9ixfXENr0aPmowNBJOAixKFac/edit?tab=t.0#heading=h.v3w1qhzgq084
           */
          transition:
            'opacity 0s 0s, transform 0s 0s, position 0s 0s, visibility 0s ease-out 0.001s',
          /**
           * Absolutely positioned and hidden items will still take up space in the parent layout.
           * This will cause an overflow issue in components like `SearchableListBox`.
           *
           * If we scoot the Actions to the left, they will not extend past the `RowItem`.
           */
          transform: `translateX(-100%)`,
          height: 0,
          pointerEvents: 'none',

          [RowItemActiveSelectors()]: {
            opacity: 1,
            visibility: 'visible',
            position: 'relative',
            transform: 'translateX(0)',
            height: 'auto',
            /**
             * We turn off pointer-events at the portal wrapper layer (see `RowItemActionsPortal` above)
             * Chevrons and non-interactive Adornments cause the wrapper to have a non-zero size and block clicks.
             * We need to turn pointer-events back on for actions so that users can interact with them.
             */
            pointerEvents: 'auto',
            /**
             * We need to override our opacity when the action is disabled to match `Button` styles
             */
            [disabledSelector('&')]: {
              opacity: tokens.opacities.disabled,
            },
          },
        },
      },
      dragState: {
        true: {
          display: 'none',
        },
      },
    },
  }
);
